import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EmpresaPadraoResponse, EmpresaVisivel, MeDto, Permissao, PreferenciaEmailResponse } from '@triade/contracts';
import { ApiError, enviarArquivo, requisitar } from '@/lib/api';
import { useSessao } from '@/lib/session';
import { useConsulta, useMutacao } from './base';

/**
 * GET /me: nome, papeis e permissoes efetivas do usuario na empresa escolhida (header X-Empresa-Id, quando ha).
 * A chave leva a empresa: trocar de empresa busca de novo as permissoes. Quem nega e a API; aqui so se esconde.
 */
export function useMe() {
  const { empresaId } = useSessao();
  return useQuery<MeDto, ApiError>({ queryKey: ['me', empresaId], queryFn: () => requisitar<MeDto>('/me') });
}

/** GET /me/empresas: alimenta o seletor de empresa (X-Empresa-Id). */
export function useEmpresas() {
  return useConsulta<EmpresaVisivel[]>(['me', 'empresas'], '/me/empresas', { semEmpresa: true });
}

/** GET /me/empresa-padrao: a empresa a abrir (a ultima escolhida, se ainda tem acesso e esta ativa; senao a primeira com acesso, que o servidor grava). So com sessao APP. */
export function useEmpresaPadrao() {
  const { accessToken, escopo } = useSessao();
  return useConsulta<EmpresaPadraoResponse>(['me', 'empresa-padrao'], '/me/empresa-padrao', {
    semEmpresa: true,
    ativo: accessToken !== null && escopo === 'APP',
  });
}

/** PUT /me/empresa-atual: grava no servidor a empresa escolhida (vale em qualquer navegador). Sem invalidar: a troca de X-Empresa-Id ja muda as chaves. */
export function useDefinirEmpresaAtual() {
  return useMutacao<void, { empresaId: string }>({
    metodo: 'PUT',
    caminho: () => '/me/empresa-atual',
    corpo: ({ empresaId }) => ({ empresaId }),
    semEmpresa: true,
  });
}

/** Permissoes efetivas na empresa atual. `pode` e falso enquanto o /me carrega (a tela nao mostra o que nao sabe permitido). */
export function usePode(): { pode: (permissao: Permissao) => boolean; carregando: boolean } {
  const me = useMe();
  const permissoes = me.data?.permissoes;
  return { pode: (p) => permissoes?.includes(p) ?? false, carregando: me.isPending };
}

/** PATCH /me: o proprio usuario altera o nome (aparado antes de enviar). Invalida /me (tela Dados e menu lateral). */
export function useAtualizarPerfil() {
  return useMutacao<MeDto, string>({
    metodo: 'PATCH',
    caminho: () => '/me',
    corpo: (nome) => ({ nome: nome.trim() }),
    invalidar: [['me']],
    semEmpresa: true,
  });
}

/** PUT /me/foto (multipart, campo `arquivo`). O web reduz a imagem antes (ver components/conta/reduzir-foto.ts). */
export function useEnviarFoto() {
  const qc = useQueryClient();
  return useMutation<MeDto, ApiError, Blob>({
    mutationFn: (arquivo) => enviarArquivo<MeDto>('/me/foto', arquivo, 'foto.jpg', 'PUT', { semEmpresa: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

/** DELETE /me/foto. */
export function useRemoverFoto() {
  return useMutacao<MeDto>({ metodo: 'DELETE', caminho: () => '/me/foto', invalidar: [['me']], semEmpresa: true });
}

/** GET /me/notificacoes-email: o usuario recebe as notificacoes do sino tambem por e-mail? `smtpConfigurado` = o servidor envia e-mail. */
export function usePreferenciaEmail() {
  return useQuery<PreferenciaEmailResponse, ApiError>({
    queryKey: ['me', 'notificacoes-email'],
    queryFn: () => requisitar<PreferenciaEmailResponse>('/me/notificacoes-email', { semEmpresa: true }),
  });
}

/** PUT /me/notificacoes-email: liga ou desliga o envio por e-mail (so o proprio usuario). */
export function useSalvarPreferenciaEmail() {
  return useMutacao<PreferenciaEmailResponse, boolean>({
    metodo: 'PUT',
    caminho: () => '/me/notificacoes-email',
    corpo: (email) => ({ email }),
    invalidar: [['me', 'notificacoes-email']],
    semEmpresa: true,
  });
}
